import {ChangeEvent, FC, KeyboardEvent, useLayoutEffect, useRef, useState} from 'react';
import {TextInput} from 'features/registration/ui/components/TextInput';
import {SBP_PHONE_PLACEHOLDER, extractSbpPhoneDigits, formatSbpPhoneDisplay} from '../../model/sbpPhone';
import {SbpInfoDrawer} from '../SbpInfoDrawer';
import styles from './SbpPhoneStep.module.css';


type Props = {
	value: string;
	onChange: (phoneDigits: string) => void;
};

const caretPosAfterDigits = (display: string, digitCount: number): number => {
	if (digitCount <= 0) {
		return 0;
	}

	let seen = 0;

	for (let i = 0; i < display.length; i++) {
		if (/\d/.test(display[i]!)) {
			seen += 1;

			if (seen === digitCount) {
				return i + 1;
			}
		}
	}

	return display.length;
};

export const SbpPhoneStep: FC<Props> = ({value, onChange}) => {
	const phoneDigits = extractSbpPhoneDigits(value);
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const [caretDigits, setCaretDigits] = useState<number | null>(null);
	const [isInfoOpen, setIsInfoOpen] = useState(false);


	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const input = event.target;
		const nextRaw = input.value;
		const nextPhone = extractSbpPhoneDigits(nextRaw);
		const caret = input.selectionStart ?? nextRaw.length;
		const digitsBeforeCaret = extractSbpPhoneDigits(nextRaw.slice(0, caret)).length;

		onChange(nextPhone);
		setCaretDigits(digitsBeforeCaret);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== 'Backspace') {
			return;
		}

		const input = event.currentTarget;
		const caret = input.selectionStart ?? 0;
		const selectionEnd = input.selectionEnd ?? 0;

		if (caret !== selectionEnd || caret === 0) {
			return;
		}

		const display = formatSbpPhoneDisplay(phoneDigits);
		const prevChar = display[caret - 1];

		if (prevChar !== ' ' && prevChar !== '-' && prevChar !== ')') {
			return;
		}

		event.preventDefault();

		const digitsBeforeCaret = extractSbpPhoneDigits(display.slice(0, caret)).length;

		if (digitsBeforeCaret === 0) {
			return;
		}

		const nextDigits = phoneDigits.slice(0, digitsBeforeCaret - 1) + phoneDigits.slice(digitsBeforeCaret);
		onChange(nextDigits);
		setCaretDigits(digitsBeforeCaret - 1);
	};

	useLayoutEffect(() => {
		if (caretDigits === null || !inputRef.current) {
			return;
		}

		const pos = caretPosAfterDigits(formatSbpPhoneDisplay(phoneDigits), caretDigits);
		inputRef.current.setSelectionRange(pos, pos);
		setCaretDigits(null);
	}, [phoneDigits, caretDigits]);

	return (
		<>
			<div className={styles.root}>
				<h2 className={styles.title}>Введите номер телефона</h2>

				<div className={styles.field}>
					<div className={styles.inputRow}>
						<span className={styles.prefix}>+ 7</span>
						<TextInput
							ref={inputRef}
							name="paymentPhone"
							className={styles.input}
							value={formatSbpPhoneDisplay(value)}
							placeholder={SBP_PHONE_PLACEHOLDER}
							inputMode="numeric"
							autoComplete="tel"
							autoFocus
							onChange={handleChange}
							onKeyDown={handleKeyDown}
							onClear={() => onChange('')}
						/>
					</div>

					<button type="button" className={styles.moreLink} onClick={() => setIsInfoOpen(true)}>
						Подробнее о СБП
					</button>
				</div>
			</div>

			<SbpInfoDrawer open={isInfoOpen} onClose={() => setIsInfoOpen(false)} />
		</>
	);
};
